/**
 * The board's pieces: the role-aware task-type filter and the task card
 * with its execution-state line. A suggested priority is never dressed as
 * an authoritative one — the card renders it as a dashed, muted row and the
 * filled badge exists only from Ready onward.
 */

import type { ReactNode } from 'react';

import type { TaskSummary } from '../api.js';
import { Button, Tag } from '../ui.js';
import { PriorityBadge, TaskStateChip } from './task-status-badges.js';
import { useWorkspaceCopy } from './use-workspace-copy.js';

// --------------------------------------------------------------------------- //
// TaskTypeFilter
// --------------------------------------------------------------------------- //

/**
 * The role-aware task-type filter: All · Development · Annotation.
 *
 * Takes the caller's server-computed visible types and renders **nothing**
 * when fewer than two are visible — an annotator gets no control, not a
 * disabled one, so the board carries no hint that another type exists. The
 * selection is passed to the server-side filter; nothing here filters rows.
 *
 * A group of pressed buttons rather than a select: two or three options,
 * always visible, one keystroke each — same pattern as the feed's
 * everyone/mine toggle, so the two read as one vocabulary.
 */
export function TaskTypeFilter({
  visibleTypes,
  value,
  onChange,
}: {
  readonly visibleTypes: readonly string[];
  /** `null` is "All". */
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
}): ReactNode {
  const copy = useWorkspaceCopy();
  if (visibleTypes.length < 2) return null;

  const choices: readonly { key: string | null; label: string }[] = [
    { key: null, label: copy.typeFilter.all },
    ...visibleTypes.map((type) => ({
      key: type,
      label: copy.types[type] ?? type,
    })),
  ];

  return (
    <div
      role="group"
      aria-label={copy.typeFilter.label}
      style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}
    >
      {choices.map((choice) => (
        <Button
          key={choice.key ?? 'all'}
          size="sm"
          variant={value === choice.key ? 'outline' : 'quiet'}
          aria-pressed={value === choice.key}
          onClick={() => {
            onChange(choice.key);
          }}
        >
          {choice.label}
        </Button>
      ))}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// TaskCard
// --------------------------------------------------------------------------- //

export function TaskCard({
  task,
  onOpen,
  showStatus = true,
}: {
  readonly task: TaskSummary;
  readonly onOpen: (taskId: string) => void;
  /**
   * Whether the card carries its own status chip.
   *
   * The board's columns already say the status, so the board passes false
   * and spends the pixels on execution state instead; the dashboard's mixed
   * lists keep the chip, because there nothing else says it.
   */
  readonly showStatus?: boolean;
}): ReactNode {
  const copy = useWorkspaceCopy();
  const isBacklog = task.status === 'BACKLOG';

  return (
    <article className="mj-task-card">
      <button
        type="button"
        onClick={() => {
          onOpen(task.id);
        }}
        className="mj-task-card-button"
        // Only an approved priority earns the edge accent; the CSS repeats
        // what the badge already says in words.
        data-priority={task.priority ?? undefined}
      >
        {/* Top: reference · priority — the scan line. */}
        <span
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* The reference is an identifier: LTR, tabular, never reshaped. */}
          <span
            className="mj-ltr"
            dir="ltr"
            style={{ font: 'var(--type-body-tabular)', color: 'var(--text-muted)' }}
          >
            {task.reference}
          </span>
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {/* The two priority treatments. Never the same component. */}
            {isBacklog && task.suggestedPriority !== null ? (
              <Tag dashed>
                {copy.task.suggested(
                  copy.priorities[task.suggestedPriority] ?? task.suggestedPriority,
                )}
                {' — '}
                {copy.task.suggestedNotApproved}
              </Tag>
            ) : null}
            {!isBacklog && task.priority !== null ? (
              <PriorityBadge priority={task.priority} />
            ) : null}
            {/* Rejected straight from the Backlog: nothing was approved,
                and inventing a level here would be a lie. Say so, mutedly. */}
            {!isBacklog && task.priority === null ? (
              <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>
                {copy.task.noPriority}
              </span>
            ) : null}
          </span>
        </span>

        {/* Main: the title. */}
        <span
          style={{
            display: 'block',
            font: 'var(--type-body-md)',
            color: 'var(--text-primary)',
            margin: '6px 0 8px',
          }}
        >
          {task.title}
        </span>

        {/* Middle: what kind of work, and how big. */}
        <span
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <Tag tone="neutral" title={copy.task.type}>
            {copy.types[task.taskType] ?? task.taskType}
          </Tag>
          {/* Size: a Latin abbreviation, LTR-isolated under RTL. */}
          <Tag tone="neutral" title={copy.propose.difficultyLabel}>
            <span className="mj-ltr" dir="ltr">
              {copy.difficulties[task.difficulty] ?? task.difficulty}
            </span>
          </Tag>
        </span>

        {/* Bottom: assignee and execution state (plus status where needed). */}
        <span
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <ExecutionStateNote task={task} />
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {showStatus ? <TaskStateChip status={task.status} /> : null}
            {task.assignee !== null ? (
              <span
                style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}
                title={copy.task.assignee}
              >
                {task.assignee.displayName}
              </span>
            ) : null}
          </span>
        </span>
      </button>
    </article>
  );
}

/**
 * The card's execution-state line: a word plus, when blocked, the visible
 * blocker references — text, never nested links, because the whole card is
 * already a button. Never colour alone: the glyphs are decoration over the
 * words, not the message.
 */
function ExecutionStateNote({ task }: { readonly task: TaskSummary }): ReactNode {
  const copy = useWorkspaceCopy();

  if (task.executionState === 'INDEPENDENT') {
    return (
      <span style={{ font: 'var(--type-caption)', color: 'var(--text-secondary)' }}>
        {copy.execution.independent}
      </span>
    );
  }

  if (task.executionState === 'DEPENDENCIES_CLEARED') {
    return (
      <span style={{ font: 'var(--type-caption)', color: 'var(--status-success-fg)' }}>
        {copy.execution.cleared}
      </span>
    );
  }

  const shown = task.blockedBy.slice(0, 2);
  const remainder = task.unresolvedCount - shown.length;

  // Blocked earns a filled tag, not just tinted text: unresolved
  // prerequisites are the one execution state that stops work.
  return (
    <span
      style={{
        font: 'var(--type-caption)',
        color: 'var(--status-warning-fg)',
        display: 'inline-flex',
        gap: 4,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <Tag tone="warning" title={copy.execution.blockedBy}>
        {copy.execution.blockedBadge}
      </Tag>{' '}
      {copy.execution.blockedBy}{' '}
      {shown.map((blocker, index) => (
        <span key={blocker.reference}>
          <span
            className="mj-ltr"
            dir="ltr"
            style={{ font: 'var(--type-body-tabular)' }}
          >
            {blocker.reference}
          </span>
          {index < shown.length - 1 ? ',' : null}
        </span>
      ))}
      {remainder > 0 ? <span>{copy.execution.more(remainder)}</span> : null}
    </span>
  );
}
